import { useEffect, useRef, useState, type ClipboardEvent, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertCircleIcon, MailIcon, KeyRoundIcon } from 'lucide-react';
import { parentAuth, ParentApiError } from '../../services/parentApi';
import { Button } from '../ui/button';
import { RESEND_COOLDOWN_S, codeErrorMessage, normaliseCode } from '../../utils/signInFlow';

const inputCls =
  'block w-full min-h-[52px] rounded-xl border border-border-strong bg-surface-raised px-4 text-lg text-ink-950 focus:outline-none focus-visible:ring-2 focus-visible:ring-focus focus:border-focus';

/**
 * Parent / guardian sign-in with an emailed 6-digit code (#268, redesigned #387).
 * UI only: it calls the same /api/parent-auth actions as before. The email step never
 * says whether an address is on file (anti-enumeration).
 */
export const ParentCodeSignIn = ({ next }: { next: string }) => {
  const navigate = useNavigate();
  const [step, setStep] = useState<'email' | 'code'>('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sentAt, setSentAt] = useState<number | null>(null);
  const [cooldown, setCooldown] = useState(0);
  const [resent, setResent] = useState(false);
  const codeInput = useRef<HTMLInputElement>(null);
  const emailInput = useRef<HTMLInputElement>(null);

  // Already signed in? Go straight on.
  useEffect(() => {
    parentAuth.session().then(() => navigate(next, { replace: true })).catch(() => undefined); // not signed in: stay
  }, [navigate, next]);

  useEffect(() => {
    if (step === 'code') codeInput.current?.focus();
  }, [step]);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = window.setTimeout(() => setCooldown(c => c - 1), 1000);
    return () => window.clearTimeout(t);
  }, [cooldown]);

  const sendCode = async (e?: FormEvent, isResend = false) => {
    e?.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await parentAuth.requestCode(email);
      setStep('code');
      setCode('');
      setSentAt(Date.now());
      setCooldown(RESEND_COOLDOWN_S);
      setResent(isResend);
    } catch (err) {
      setError(err instanceof ParentApiError ? err.message : 'Something went wrong. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  const verify = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await parentAuth.verify(email, code);
      navigate(next, { replace: true });
    } catch (err) {
      const status = err instanceof ParentApiError ? err.status : -1;
      setError(codeErrorMessage(status, sentAt, Date.now(), err instanceof ParentApiError ? err.message : undefined));
      setBusy(false);
    }
  };

  const onPaste = (e: ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    setCode(normaliseCode(e.clipboardData.getData('text')));
  };

  const useDifferentEmail = () => {
    setStep('email');
    setError(null);
    setCode('');
    setResent(false);
    window.setTimeout(() => emailInput.current?.focus(), 0);
  };

  const stepLabel = (n: number, text: string, active: boolean) => (
    <li className={`flex items-start gap-3 ${active ? 'text-ink-950' : 'text-ink-600'}`}>
      <span
        aria-hidden="true"
        className={`flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full text-sm font-semibold ${
          active ? 'bg-button-primary text-button-primary-label' : 'bg-surface-sunken text-ink-700'
        }`}
      >
        {n}
      </span>
      <span className={`pt-0.5 text-[1rem] ${active ? 'font-semibold' : ''}`}>{text}</span>
    </li>
  );

  return (
    <div>
      <ol className="space-y-3" aria-label="How to sign in">
        {stepLabel(1, 'Enter the email the school has for you', step === 'email')}
        {stepLabel(2, 'Check your email for a 6-digit code', step === 'code')}
        {stepLabel(3, 'Type the code here to see your documents', false)}
      </ol>

      {error && (
        <div className="mt-5 flex gap-2 rounded-xl border border-critical bg-critical-quiet p-3 text-[1rem] text-ink-950" role="alert">
          <AlertCircleIcon className="h-5 w-5 flex-shrink-0" aria-hidden="true" />
          <span>{error}</span>
        </div>
      )}

      {step === 'email' ? (
        <form onSubmit={sendCode} className="mt-6 space-y-4">
          <div>
            <label htmlFor="parent-email" className="mb-1.5 block text-[1rem] font-medium text-ink-950">
              Your email address
            </label>
            <input
              id="parent-email"
              ref={emailInput}
              type="email"
              inputMode="email"
              autoComplete="email"
              autoCapitalize="none"
              spellCheck={false}
              required
              value={email}
              onChange={e => setEmail(e.target.value)}
              className={inputCls}
            />
          </div>
          <Button type="submit" size="lg" className="w-full min-h-[52px]" disabled={busy || !email.trim()}>
            <MailIcon aria-hidden="true" />
            {busy ? 'Sending…' : 'Email me a code'}
          </Button>
        </form>
      ) : (
        <form onSubmit={verify} className="mt-6 space-y-4">
          <p className="rounded-xl bg-surface-sunken p-3 text-[1rem] text-ink-800" role="status">
            {resent ? 'We sent a new code. Use the newest email. ' : ''}
            If <strong className="break-all">{email.trim()}</strong> is on file, a code is on its way. It can take a
            minute. Check your junk folder too.
          </p>
          <div>
            <label htmlFor="parent-code" className="mb-1.5 block text-[1rem] font-medium text-ink-950">
              6-digit code
            </label>
            <input
              id="parent-code"
              ref={codeInput}
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              pattern="[0-9]{6}"
              required
              value={code}
              onPaste={onPaste}
              onChange={e => setCode(normaliseCode(e.target.value))}
              className={`${inputCls} text-center text-2xl tracking-[0.5em]`}
              aria-describedby="parent-code-help"
            />
            <p id="parent-code-help" className="mt-1.5 text-[1rem] text-ink-600">
              The code works once and lasts 10 minutes.
            </p>
          </div>
          <Button type="submit" size="lg" className="w-full min-h-[52px]" disabled={busy || code.length !== 6}>
            <KeyRoundIcon aria-hidden="true" />
            {busy ? 'Checking…' : 'Sign in'}
          </Button>
          <div className="flex flex-col gap-1 sm:flex-row sm:justify-between">
            <Button
              type="button"
              variant="ghost"
              className="min-h-[48px] justify-start px-3 text-[1rem]"
              onClick={() => sendCode(undefined, true)}
              disabled={busy || cooldown > 0}
            >
              {cooldown > 0 ? `Send me a new code (${cooldown}s)` : 'Send me a new code'}
            </Button>
            <Button type="button" variant="ghost" className="min-h-[48px] justify-start px-3 text-[1rem]" onClick={useDifferentEmail}>
              Use a different email
            </Button>
          </div>
        </form>
      )}

      <p className="mt-6 text-[1rem] text-ink-600">
        Not getting a code? Use the email address the school sends you letters at, or contact the Learning Diversity
        team at the school.
      </p>
    </div>
  );
};
